import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { saveContactSubmission } from '../utils/supabase';

const Contact = () => {
  const [formData, setFormData] = useState({
    orgName: '',
    name: '',
    number: '',
    email: '',
    services: '',
    message: ''
  });
  const [hasConsent, setHasConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleServiceChange = (value) => {
    setFormData({
      ...formData,
      services: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Save to Supabase
      await saveContactSubmission(formData);
      
      toast.success('Thank you! We\'ll get back to you soon.');
      setFormData({
        orgName: '',
        name: '',
        number: '',
        email: '',
        services: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Sales Inquiries',
      value: 'sales@smartbeeps.com',
      link: 'mailto:sales@smartbeeps.com'
    },
    {
      icon: Mail,
      title: 'Support',
      value: 'support@smartbeeps.com',
      link: 'mailto:support@smartbeeps.com'
    },
    {
      icon: Phone,
      title: 'Phone',
      value: '+91 7569820488',
      link: 'tel:+917569820488'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-cyan-500/20 via-transparent to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Get In Touch
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Let's discuss how Smartbeeps can transform your business communication
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {contactInfo.map((info, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl mb-6 shadow-lg">
                    <info.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{info.title}</h3>
                  <a 
                    href={info.link} 
                    className="text-blue-600 hover:text-blue-700 font-medium transition-colors text-lg"
                  >
                    {info.value}
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Contact Form */}
          <div className="max-w-3xl mx-auto">
            <Card className="border-none shadow-2xl">
              <CardContent className="p-8 md:p-12">
                <div className="text-center mb-10">
                  <h2 className="text-3xl font-bold text-slate-900 mb-3">
                    Send Us a Message
                  </h2>
                  <p className="text-slate-600">
                    Fill out the form below and our team will get back to you within 24 hours
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="orgName" className="text-slate-700 font-semibold">Organization Name *</Label>
                      <Input
                        id="orgName"
                        name="orgName"
                        type="text"
                        placeholder="Your Company Name"
                        value={formData.orgName}
                        onChange={handleChange}
                        required
                        className="h-12 border-slate-300 focus:border-blue-500 transition-colors"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-700 font-semibold">Your Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="h-12 border-slate-300 focus:border-blue-500 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="number" className="text-slate-700 font-semibold">Phone Number *</Label>
                      <Input
                        id="number"
                        name="number"
                        type="tel"
                        placeholder="+91 98765 43210"
                        value={formData.number}
                        onChange={handleChange}
                        required
                        className="h-12 border-slate-300 focus:border-blue-500 transition-colors"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-700 font-semibold">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="john@company.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="h-12 border-slate-300 focus:border-blue-500 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="services" className="text-slate-700 font-semibold">Service Interest *</Label>
                    <Select onValueChange={handleServiceChange} value={formData.services} required>
                      <SelectTrigger className="h-12 border-slate-300 focus:border-blue-500 transition-colors">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sms">SMS</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp Business</SelectItem>
                        <SelectItem value="voice">Voice</SelectItem>
                        <SelectItem value="rcs">RCS</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="voice-bots">Voice Bots</SelectItem>
                        <SelectItem value="whatsapp-bots">WhatsApp Bots</SelectItem>
                        <SelectItem value="multiple">Multiple Services</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-slate-700 font-semibold">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Tell us about your requirements..."
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="border-slate-300 focus:border-blue-500 transition-colors resize-none"
                    />
                  </div>

                  <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                      By contacting us, you agree to our{' '}
                      <Link to="/terms" className="text-blue-600 hover:text-blue-700 font-medium">
                        Terms and Conditions
                      </Link>{' '}
                      and that you have read our{' '}
                      <Link to="/privacy" className="text-blue-600 hover:text-blue-700 font-medium">
                        Privacy Policy
                      </Link>.
                    </p>

                    <div className="flex items-start space-x-3">
                      <input
                        id="consent"
                        name="consent"
                        type="checkbox"
                        required
                        checked={hasConsent}
                        onChange={(e) => setHasConsent(e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <Label htmlFor="consent" className="text-sm text-slate-700">
                        I hearby authorize to send notifications on SMS/RCS/Messages/Whatsapp/Promotional/ Informational Messages.
                      </Label>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting || !hasConsent}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <Send className="ml-2 h-5 w-5" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;